"use client"

import { clearActiveSession, validateTokenForWallet } from "@src/actions/auth"
import { resolvePostLoginRedirectTarget } from "@src/config/routes"
import { syncWalletAuthCache } from "@src/hooks/useWalletAuth"
import { useWalletSnapshot } from "@src/hooks/useWalletSnapshot"
import { appBootstrapMachine } from "@src/machines/appBootstrapMachine"
import { logger } from "@src/utils/logger"
import { useQueryClient } from "@tanstack/react-query"
import { useActorRef, useSelector } from "@xstate/react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { type ReactNode, createContext, useContext, useEffect } from "react"
import { type ActorRefFrom, type SnapshotFrom, fromPromise } from "xstate"

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

type BootstrapActorRef = ActorRefFrom<typeof appBootstrapMachine>

const BootstrapContext = createContext<BootstrapActorRef | null>(null)

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Returns the bootstrap machine actor ref for sending events */
export function useBootstrapActor(): BootstrapActorRef {
  const actor = useContext(BootstrapContext)
  if (!actor) {
    throw new Error(
      "useBootstrapActor must be used within AppBootstrapProvider"
    )
  }
  return actor
}

/** Returns a selected slice of bootstrap machine state */
export function useBootstrapSelector<T>(
  selector: (snapshot: SnapshotFrom<typeof appBootstrapMachine>) => T
): T {
  const actor = useBootstrapActor()
  return useSelector(actor, selector)
}

/** Returns the current machine state value string */
export function useBootstrapState(): string {
  return useBootstrapSelector((snap) => snap.value as string)
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AppBootstrapProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()

  const { walletState, restoreStatus } = useWalletSnapshot()

  const actorRef = useActorRef(
    appBootstrapMachine.provide({
      actions: {
        navigateToLogin: ({ context }) => {
          const param = context.routeIntent
            ? `?redirect=${encodeURIComponent(context.routeIntent)}`
            : ""
          logger.trace("[bootstrap] nav → /login", {
            routeIntent: context.routeIntent,
          })
          router.replace(`/login${param}`)
        },
        navigateFromLoginIfNeeded: ({ context }) => {
          if (context.pathname !== "/login") return
          router.replace(resolvePostLoginRedirectTarget(context.searchRedirect))
        },
        navigateToBanned: () => {
          router.replace("/banned")
        },
        refreshRouter: () => {
          router.refresh()
        },
        clearSession: () => {
          void clearActiveSession()
        },
      },
      actors: {
        checkSession: fromPromise(
          async ({
            input,
          }: { input: { address: string; chainType: string } }) => {
            return validateTokenForWallet(input.address, input.chainType)
          }
        ),
      },
    }),
    { input: { pathname } }
  )
  const bootstrapAddress = useSelector(actorRef, (snap) => snap.context.address)
  const bootstrapChainType = useSelector(
    actorRef,
    (snap) => snap.context.chainType
  )
  const bootstrapSessionResult = useSelector(
    actorRef,
    (snap) => snap.context.sessionResult
  )

  // ==========================================================================
  // Effect A: Wallet state bridge — machine detects transitions via guards
  // ==========================================================================
  useEffect(() => {
    actorRef.send({
      type: "WALLET_STATE",
      address: walletState.address ?? null,
      chainType: walletState.chainType ?? null,
      restoreStatus,
    })
  }, [walletState.address, walletState.chainType, restoreStatus, actorRef])

  // ==========================================================================
  // Effect B: Pathname bridge
  // ==========================================================================
  useEffect(() => {
    actorRef.send({
      type: "PATHNAME_CHANGED",
      pathname,
      searchRedirect: searchParams.get("redirect"),
    })
  }, [pathname, searchParams, actorRef])

  // ==========================================================================
  // Effect C: Keep legacy token_validation cache in sync with bootstrap auth.
  // Some UI still reads useWalletAuth/useConnectWallet, so without this bridge
  // it can stay stuck on a fresh "unauthenticated" result after verification.
  // ==========================================================================
  useEffect(() => {
    syncWalletAuthCache(
      queryClient,
      bootstrapAddress,
      bootstrapChainType,
      bootstrapSessionResult
    )
  }, [
    queryClient,
    bootstrapAddress,
    bootstrapChainType,
    bootstrapSessionResult,
  ])

  return (
    <BootstrapContext.Provider value={actorRef}>
      {children}
    </BootstrapContext.Provider>
  )
}
